import PropTypes from 'prop-types';

// Common PropTypes for reusable components
export const ImagePropTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
  onClick: PropTypes.func,
  onLoad: PropTypes.func,
  onError: PropTypes.func,
};

export const ButtonPropTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  variant: PropTypes.oneOf(['primary', 'secondary', 'outline', 'ghost']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
};

export const CardPropTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  image: PropTypes.string,
  onClick: PropTypes.func,
};

export const FormFieldPropTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf([
    'text',
    'email',
    'password',
    'tel',
    'textarea',
    'select',
  ]),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.string,
  className: PropTypes.string,
};

export const NavigationItemPropTypes = {
  label: PropTypes.string.isRequired,
  href: PropTypes.string,
  onClick: PropTypes.func,
  isActive: PropTypes.bool,
  children: PropTypes.node,
};

export const ServiceItemPropTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  icon: PropTypes.node,
  link: PropTypes.string,
  features: PropTypes.arrayOf(PropTypes.string),
};

export const TestimonialPropTypes = {
  name: PropTypes.string.isRequired,
  company: PropTypes.string,
  content: PropTypes.string.isRequired,
  rating: PropTypes.number,
  image: PropTypes.string,
};

export const GalleryImagePropTypes = {
  id: PropTypes.string.isRequired,
  thumbnail: PropTypes.string.isRequired,
  full: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
};

export const EventPropTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  location: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  registrationLink: PropTypes.string,
};

export const PersonPropTypes = {
  name: PropTypes.string.isRequired,
  role: PropTypes.string,
  image: PropTypes.string,
  bio: PropTypes.string,
  socialLinks: PropTypes.object,
};

export const TypographyPropTypes = {
  heading1: PropTypes.string,
  heading2: PropTypes.string,
  heading3: PropTypes.string,
  heading4: PropTypes.string,
  subtitle: PropTypes.string,
  body: PropTypes.string,
  small: PropTypes.string,
  button: PropTypes.string,
  caption: PropTypes.string,
};

// Validation functions
export const validateEmail = email => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = phone => {
  const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/[\s\-()]/g, ''));
};

export const validateRequired = value => {
  return value && value.trim().length > 0;
};

export const validateMinLength = (value, minLength) => {
  return value && value.length >= minLength;
};

export const validateMaxLength = (value, maxLength) => {
  return value && value.length <= maxLength;
};

// Custom PropTypes
export const CustomPropTypes = {
  email: (props, propName, componentName) => {
    if (props[propName] && !validateEmail(props[propName])) {
      return new Error(
        `Invalid prop '${propName}' supplied to '${componentName}'. Expected a valid email address.`
      );
    }
  },

  phone: (props, propName, componentName) => {
    if (props[propName] && !validatePhone(props[propName])) {
      return new Error(
        `Invalid prop '${propName}' supplied to '${componentName}'. Expected a valid phone number.`
      );
    }
  },

  requiredString: (props, propName, componentName) => {
    if (!validateRequired(props[propName])) {
      return new Error(
        `Required prop '${propName}' was not supplied to '${componentName}'.`
      );
    }
  },
};
